import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, filter, map, of, switchMap, withLatestFrom } from 'rxjs';
import { AppState } from 'src/app/core/store/state';
import { areGenerateClausePayloadsEqual } from 'src/app/core/utils/doc-generation.utils';
import { DocumentGenerationWorker } from 'src/app/core/workers/document-generation.worker';
import { DocumentGenerationApiService } from '../services/document-generation-api.service';
import { DocumentGenerationActions } from './actions';
import { selectGenerateClauseListResponse, selectLastGenerateClauseListPayload } from './selectors';

@Injectable()
export class FeatureEffects {
  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private documentGenerationApiService: DocumentGenerationApiService,
    private documentGenerationWorker: DocumentGenerationWorker,
  ) {}

  generateClauseList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentGenerationActions.generateClauseList),
      withLatestFrom(
        this.store.select(selectGenerateClauseListResponse),
        this.store.select(selectLastGenerateClauseListPayload),
      ),
      switchMap(([props, previousGenerateClauseListResponse, previousPayload]) => {
        if (props.createDocumentImmediately) {
          this.store.dispatch(DocumentGenerationActions.closeGenerateDocumentModal());
        }

        if (
          previousGenerateClauseListResponse &&
          areGenerateClausePayloadsEqual(previousPayload, props.payload)
        ) {
          if (props.createDocumentImmediately) {
            this.store.dispatch(
              DocumentGenerationActions.generateDocument({
                generateClauseListResponse: previousGenerateClauseListResponse,
              }),
            );
          }

          return of(
            DocumentGenerationActions.generateClauseListSuccess({
              payload: props.payload,
              generateClauseListResponse: previousGenerateClauseListResponse,
            }),
          );
        } else {
          // payload was changed
          this.store.dispatch(DocumentGenerationActions.resetGenerateDocumentResults());

          return this.documentGenerationApiService.generateClauseList$(props.payload).pipe(
            map((generateClauseListResponse) => {
              if (props.createDocumentImmediately) {
                this.store.dispatch(
                  DocumentGenerationActions.generateDocument({
                    generateClauseListResponse: generateClauseListResponse,
                  }),
                );
              }
              return DocumentGenerationActions.generateClauseListSuccess({
                payload: props.payload,
                generateClauseListResponse,
              });
            }),
            catchError(() => {
              return of(
                DocumentGenerationActions.generateClauseListFailure({
                  payload: props.payload,
                }),
              );
            }),
          );
        }
      }),
    ),
  );

  retryGenerateClauseList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentGenerationActions.retryGenerateClauseList),
      withLatestFrom(this.store.select(selectLastGenerateClauseListPayload)),
      filter(([, payload]) => !!payload),
      map(([, payload]) =>
        DocumentGenerationActions.generateClauseList({
          payload: payload!,
          createDocumentImmediately: false,
        }),
      ),
    ),
  );

  generateDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentGenerationActions.generateDocument),
      switchMap((props) => {
        return this.documentGenerationApiService
          .generateDocument$(props.generateClauseListResponse)
          .pipe(
            map((generateDocumentResponse) => {
              this.documentGenerationWorker.init(generateDocumentResponse);
              this.store.dispatch(
                DocumentGenerationActions.setGenerateDocumentResponse({
                  generateDocumentResponse,
                }),
              );
              this.store.dispatch(DocumentGenerationActions.closeGenerateDocumentModal());
              this.store.dispatch(DocumentGenerationActions.startAskingGenerateDocumentStatus());
              return DocumentGenerationActions.generateDocumentStarted();
            }),
            catchError((response) => {
              return of(DocumentGenerationActions.generateDocumentFailure());
            }),
          );
      }),
    ),
  );
}
